import { createAsyncThunk } from "@reduxjs/toolkit";
import toViewEntityType from "components/WritingEditing/components/DocumentEditor/utils/toViewEntityType";
import { FOUR_MEGABYTES } from "constants/add-in";
import { isEmpty } from "lodash";
import { addToJobQueue, removeFromJobQueue } from "slices/app/appSlice";
import { getFileNameFromPath, METADATA_KEY } from "utils/add-in";
import { REQUIRED_METADATA_VALUES } from "utils/add-in/utilities";

import { documentEdited } from "../currentDocument/currentDocumentSlice";
import {
  removeFromProperties,
  resetProperties,
  setDocumentMetadata,
  setFileInfo,
} from "./metadataSlice";

export const readDocumentFileInfo = createAsyncThunk(
  "add-in/read-file-info",
  async (_, { dispatch }) => {
    const url = Office.context.document.url ?? "";
    const { fileName, fileType } = getFileNameFromPath(url);

    const fileSize = await new Promise((resolve, reject) => {
      Office.context.document.getFileAsync(
        Office.FileType.Compressed,
        { sliceSize: FOUR_MEGABYTES },
        (result) => {
          if (result.status === Office.AsyncResultStatus.Succeeded) {
            const { size } = result.value;
            result.value.closeAsync();
            resolve(size);
          } else {
            reject(result.error);
          }
        }
      );
    });

    dispatch(setFileInfo({ fileName, fileType, fileSize }));
  }
);

export const readDocumentMetadata = createAsyncThunk(
  "add-in/read-metadata",
  async (_, { dispatch }) => {
    dispatch(addToJobQueue("readDocumentMetadata"));

    await dispatch(readDocumentFileInfo());

    await global.Word.run(async (context) => {
      context.document.properties.load("lastSaveTime");

      const customProps = context.document.properties.customProperties;
      customProps.load("items");
      await context.sync();

      customProps.items.forEach((p) => {
        p.load(["key", "value", "type"]);
      });
      await context.sync();

      const lastSaveTime = context.document.properties.lastSaveTime;
      dispatch(documentEdited(lastSaveTime?.toISOString() ?? null));

      const documentMetadata = {};

      customProps.items.forEach(({ key, value }) => {
        const isVtValue = key.startsWith(METADATA_KEY);

        if (isVtValue) {
          const regex = new RegExp(`^${METADATA_KEY}([-]+)`, "gi");
          const finalKey = key.replace(regex, "");
          documentMetadata[finalKey] = value;
        }
      });

      if (documentMetadata.entityType) {
        documentMetadata.entityType = toViewEntityType(
          documentMetadata.entityType
        );
      }

      console.warn("🟪 documentMetadata", documentMetadata);
      dispatch(setDocumentMetadata(documentMetadata));

      dispatch(removeFromJobQueue("readDocumentMetadata"));
    });
  }
);

// `silent` skips the job queue for callers that render their own progress
// indicator, so the full-screen load mask does not stack on top of it. It is
// stripped from the metadata written to the document's custom properties.
export const writeDocumentMetadata = createAsyncThunk(
  "add-in/write-metadata",
  async (props, { dispatch }) => {
    const { silent = false, ...metadata } = props || {};
    const releaseJobQueue = () => {
      if (!silent) {
        dispatch(removeFromJobQueue("writeDocumentMetadata"));
      }
    };

    if (!silent) {
      dispatch(addToJobQueue("writeDocumentMetadata"));
    }

    if (isEmpty(metadata)) {
      releaseJobQueue();
      return false;
    }
    console.warn("🟪 writing documentMetadata", metadata);

    try {
      await Word.run(async (context) => {
        const { customProperties } = context.document.properties;
        const keys = Object.keys(metadata);

        for (const key of keys) {
          customProperties.add(`${METADATA_KEY}-${key}`, metadata[key]);
        }

        await context.sync();
        context.document.save();
        await context.sync();
      });
      return true;
    } finally {
      releaseJobQueue();
    }
  }
);

export const removeFromDocumentMetaData = createAsyncThunk(
  "add-in/remove-from-metadata",
  async (key, { dispatch }) => {
    return new Promise(async (resolve) => {
      await Word.run(async (context) => {
        const { customProperties } = context.document.properties;
        const finalKey = `${METADATA_KEY}-${key}`;
        const prop = customProperties.getItemOrNullObject(finalKey);
        await context.sync();

        if (!prop.isNullObject) {
          prop.delete();
          await context.sync();
          resolve(true);
          dispatch(removeFromProperties(key));
        } else {
          resolve(false);
        }
      });
    });
  }
);

export const removeAllMetadata = createAsyncThunk(
  "add-in/remove-all-metadata",
  async (_, { dispatch }) => {
    await Word.run(async (context) => {
      const { customProperties } = context.document.properties;

      for (const key of REQUIRED_METADATA_VALUES) {
        const finalKey = `${METADATA_KEY}-${key}`;
        const prop = customProperties.getItemOrNullObject(finalKey);
        await context.sync();

        if (!prop.isNullObject) {
          prop.delete();
          await context.sync();
        }
      }

      context.document.save();
      await context.sync();

      dispatch(resetProperties());
    });
  }
);
